import React from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  HStack,
  IconButton,
  Tooltip,
  useColorMode,
  Hide,
  Show,
} from "@chakra-ui/react";
import {
  ViewOffIcon,
  MoonIcon,
  SunIcon,
  AddIcon,
  MinusIcon,
  RepeatIcon,
  ArrowBackIcon,
  HamburgerIcon,
} from "@chakra-ui/icons";
import { TFunction } from "i18next";
import { ContentItem } from "../libs/types";

interface ChapterMenuProps {
  currentContentItem: ContentItem;
  exitFullPageMode: () => void;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  resetFontSize: () => void;
  fontSize: number;
  roundedFontSize: number;
  isDefaultSize: boolean;
  t: TFunction;
  toggleColorMode?: () => void;
  bookTitle?: string;
  onOpenTableOfContents?: () => void;
}

const ChapterMenu: React.FC<ChapterMenuProps> = ({
  currentContentItem,
  exitFullPageMode,
  increaseFontSize,
  decreaseFontSize,
  resetFontSize,
  fontSize,
  roundedFontSize,
  isDefaultSize,
  t,
  toggleColorMode: externalToggleColorMode,
  bookTitle = "",
  onOpenTableOfContents,
}) => {
  const { colorMode, toggleColorMode: internalToggleColorMode } =
    useColorMode();
  // Use the provided toggleColorMode or fall back to the internal one
  const toggleColorMode = externalToggleColorMode || internalToggleColorMode;

  return (
    <Box
      width="100%"
      bg="transparent"
      backgroundColor="transparent"
      borderBottom="none"
      px={4}
      mb={colorMode === "light" ? 0 : 2}
      sx={{
        background: "transparent !important",
        "::before": {
          content: "''",
          display: "none",
        },
        "::after": {
          content: "''",
          display: "none",
        },
      }}
    >
      {/* Book title at the top */}
      <Flex justify="center" align="center" py={3} background="transparent">
        <Text
          fontSize="xl"
          fontWeight="bold"
          textAlign="center"
          color={colorMode === "light" ? "gray.700" : "white"}
        >
          {bookTitle || currentContentItem.chapter}
        </Text>
      </Flex>

      {/* Font Size Controls from ReadBook */}
      <Flex
        justify={{ base: "center", md: "flex-end" }}
        align="center"
        mb={2}
        py={2}
        px={{ base: 2, md: 0 }}
        background="transparent"
      >
        <HStack
          spacing={{ base: 2, md: 3 }}
          background="transparent"
          width={{ base: "100%", md: "auto" }}
          justifyContent={{ base: "space-between", md: "flex-end" }}
        >
          {/* Exit button with home/back icon */}
          <Tooltip label={t("readBook.exitFullscreen", "Exit Reading Mode")}>
            <IconButton
              aria-label={t("readBook.exitFullscreen", "Exit Reading Mode")}
              icon={<ArrowBackIcon boxSize={4} />}
              size="md"
              isRound
              variant={colorMode === "light" ? "outline" : "ghost"}
              onClick={exitFullPageMode}
              sx={
                colorMode === "dark"
                  ? {
                      bg: "transparent",
                      color: "white",
                      _hover: { bg: "rgba(255,255,255,0.08)" },
                    }
                  : {}
              }
            />
          </Tooltip>

          {/* Table of Contents button */}
          {onOpenTableOfContents && (
            <Tooltip label={t("readBook.tableOfContents", "Table of Contents")}>
              <IconButton
                aria-label={t("readBook.tableOfContents", "Table of Contents")}
                icon={<HamburgerIcon boxSize={4} />}
                size="md"
                isRound
                variant={colorMode === "light" ? "outline" : "ghost"}
                onClick={onOpenTableOfContents}
                sx={
                  colorMode === "dark"
                    ? {
                        bg: "transparent",
                        color: "white",
                        _hover: { bg: "rgba(255,255,255,0.08)" },
                      }
                    : {}
                }
              />
            </Tooltip>
          )}

          {/* Color mode toggle */}
          <Tooltip
            label={
              colorMode === "light"
                ? t("readBook.darkMode", "Switch to dark mode")
                : t("readBook.lightMode", "Switch to light mode")
            }
          >
            <IconButton
              aria-label={
                colorMode === "light"
                  ? t("readBook.darkMode", "Switch to dark mode")
                  : t("readBook.lightMode", "Switch to light mode")
              }
              icon={
                colorMode === "light" ? (
                  <MoonIcon boxSize={4} />
                ) : (
                  <SunIcon boxSize={4} />
                )
              }
              size="md"
              isRound
              variant={colorMode === "light" ? "outline" : "ghost"}
              onClick={toggleColorMode}
              sx={
                colorMode === "dark"
                  ? {
                      bg: "transparent",
                      color: "white",
                      _hover: { bg: "rgba(255,255,255,0.08)" },
                    }
                  : {}
              }
            />
          </Tooltip>

          {/* Font size controls */}
          <Tooltip label={t("readBook.decreaseFontSize", "Decrease font size")}>
            <IconButton
              aria-label={t("readBook.decreaseFontSize", "Decrease font size")}
              icon={<MinusIcon boxSize={5} />}
              size="md"
              isRound
              variant={colorMode === "light" ? "outline" : "ghost"}
              onClick={decreaseFontSize}
              isDisabled={roundedFontSize <= 0.8}
              sx={
                colorMode === "dark"
                  ? {
                      bg: "transparent",
                      color: "white",
                      _hover: { bg: "rgba(255,255,255,0.08)" },
                    }
                  : {}
              }
            />
          </Tooltip>

          <Text
            fontWeight="medium"
            minWidth="50px"
            textAlign="center"
            color={colorMode === "light" ? "gray.700" : "gray.200"}
          >
            {Math.round(fontSize * 100)}%
          </Text>

          <Tooltip label={t("readBook.increaseFontSize", "Increase font size")}>
            <IconButton
              aria-label={t("readBook.increaseFontSize", "Increase font size")}
              icon={<AddIcon boxSize={5} />}
              size="md"
              isRound
              variant={colorMode === "light" ? "outline" : "ghost"}
              onClick={increaseFontSize}
              isDisabled={roundedFontSize >= 1.8}
              sx={
                colorMode === "dark"
                  ? {
                      bg: "transparent",
                      color: "white",
                      _hover: { bg: "rgba(255,255,255,0.08)" },
                    }
                  : {}
              }
            />
          </Tooltip>

          <Tooltip label={t("readBook.resetFontSize", "Reset font size")}>
            <IconButton
              aria-label={t("readBook.resetFontSize", "Reset font size")}
              icon={<RepeatIcon boxSize={4} />}
              size="md"
              isRound
              variant={colorMode === "light" ? "outline" : "ghost"}
              onClick={resetFontSize}
              isDisabled={isDefaultSize}
              sx={
                colorMode === "dark"
                  ? {
                      bg: "transparent",
                      color: "white",
                      _hover: { bg: "rgba(255,255,255,0.08)" },
                    }
                  : {}
              }
            />
          </Tooltip>
        </HStack>
      </Flex>
    </Box>
  );
};

export default ChapterMenu;
